import * as React from 'react';
import { FilterInterface } from '../../Filter/FilterInterface';
import { RangeFilterValueType } from '../../Filter/RangeFilter';
import { RangeFilterValue } from '../../Filter/RangeFilterValue';
import { PrepareLabelCallback } from '../RangeSlider/Handle';
import { OptionsType } from './AbstractFilterView';
import { FilterDisableEvent } from './FilterDisableEvent';
import { FilterEvent } from './FilterEvent';
import { MinMaxRangeFilter } from './MinMaxRangeFilter';
import { AbstractRangeFilterView } from './Range/AbstractRangeFilterView';
import { RangeSliderFilter, RangeSliderFilterProps } from './RangeSliderFilter';

export interface CombinedRangeFilterProps<VALUE_TYPE, OPTION_TYPE> extends RangeSliderFilterProps<VALUE_TYPE, OPTION_TYPE> {
    snapToOption?: boolean
    prepareHandleLabel?: PrepareLabelCallback
}

interface DistanceCombination {
    distance: number
    value: number
}

export class CombinedRangeFilter<VALUE_TYPE extends RangeFilterValueType,
    OPTION_TYPE = number,
    P extends CombinedRangeFilterProps<VALUE_TYPE, OPTION_TYPE> = CombinedRangeFilterProps<VALUE_TYPE, OPTION_TYPE>>
    extends AbstractRangeFilterView<VALUE_TYPE, OPTION_TYPE, P> {
    constructor(props: Readonly<P>) {
        super(props);
        this.interceptChange = this.interceptChange.bind(this);
        this.prepareHandleLabel = this.prepareHandleLabel.bind(this);
    }

    protected renderInputInner(
        props: P,
        filter: FilterInterface<RangeFilterValueType>,
        options: OptionsType<OPTION_TYPE>,
        bounds: RangeFilterValueType,
        currentValue: RangeFilterValueType
    ): JSX.Element | null {
        return <div className="pf-combined-range-wrapper">
            <MinMaxRangeFilter<VALUE_TYPE, OPTION_TYPE>
                {...this.props}
                onChange={this.interceptChange}
                hideDisable={true}/>
            <RangeSliderFilter<VALUE_TYPE, OPTION_TYPE>
                {...this.props}
                onChange={this.interceptChange}
                hideDisable={true}
                prepareHandleLabel={this.prepareHandleLabel}/>
        </div>;
    }

    protected interceptChange(event: FilterEvent<RangeFilterValueType, any> | FilterDisableEvent<any>) {
        const props = this.props;
        this.assertAttrOrFilter(props);

        if (!props.snapToOption || event instanceof FilterDisableEvent) {
            super.handleChange(event);

            return;
        }

        const value = event.value;
        if (value !== undefined) {
            const attr = props.filter.keyPath;
            const snappedValue = this.findNearestRangeFilterValue(value, props.options);

            props.onChange(new FilterEvent<VALUE_TYPE, any>(undefined, snappedValue as VALUE_TYPE), attr, props.filter);
        } else {
            super.handleChange(event);

            return;
        }
    }

    protected handleChange<F = RangeFilterValueType>(event: FilterEvent<F, any>) {
        throw new Error('Should not be called. interceptChange() was expected');
    }

    private prepareHandleLabel(value: number): string {
        const props = this.props;
        const preparedValue = (props.snapToOption) ? this.findNearestOption(value, props.options) : value;
        const prepareHandleLabel = props.prepareHandleLabel;
        if (prepareHandleLabel) {
            return prepareHandleLabel(preparedValue);
        } else {
            return preparedValue.toLocaleString();
        }
    }

    private findNearestRangeFilterValue(
        value: RangeFilterValueType,
        options: OptionsType<OPTION_TYPE>
    ): RangeFilterValueType {
        return new RangeFilterValue(
            this.findNearestOption(value.min, options),
            this.findNearestOption(value.max, options),
        );
    }

    private findNearestOption(filterValue: number, options: OptionsType<OPTION_TYPE>): number {
        const col = this.getOptionsAsNumbers(options);
        const initialValue: DistanceCombination = {
            value: 0,
            distance: Number.MAX_SAFE_INTEGER
        };

        if (!isFinite(filterValue)) {
            return Math.max(...col);
        }

        const bestMatch: DistanceCombination = col.reduce(
            (p: DistanceCombination, value: number): DistanceCombination => {
                const distance = Math.abs(filterValue - value);

                if (p.distance <= distance) {
                    return p;
                } else {
                    return {distance, value};
                }
            },
            initialValue
        );

        return bestMatch.value;
    }
}
